import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Badge,
} from "../../components/ui";
import { useSeo } from "../../lib/seo";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

interface Sub {
  name: string;
  purpose: string;
  data_categories: string;
  location: string;
  baa: "Signed" | "N/A — no PHI" | "On request";
}

const SUBS: Sub[] = [
  { name: "Supabase (Postgres, Storage, Vault, pgmq)", purpose: "Primary application database, storage, secret vault, queue.", data_categories: "Customer content (incl. PHI), audit log, account data", location: "US (AWS us-west-2 Oregon)", baa: "Signed" },
  { name: "Amazon Web Services (Bedrock)", purpose: "AI inference (Claude Sonnet 4.6).", data_categories: "De-identified prompts only (Safe Harbor)", location: "US (HIPAA-eligible region)", baa: "Signed" },
  { name: "Amazon Web Services (S3 backup)", purpose: "Off-site backup of production data.", data_categories: "Customer content (encrypted)", location: "US", baa: "Signed" },
  { name: "Adobe Sign (Acrobat Sign)", purpose: "Electronic signature on recommendation bundles and state board filings.", data_categories: "Signer identity, signed PDF artifacts (may contain PHI)", location: "US", baa: "Signed" },
  { name: "Intuit QuickBooks Online", purpose: "Invoice drafting from time entries and bundle outcomes.", data_categories: "Facility-level billing metadata. No PHI on invoice line items.", location: "US", baa: "N/A — no PHI" },
  { name: "Google Cloud (Calendar API)", purpose: "Two-way calendar sync via OAuth.", data_categories: "Calendar event titles/times. No PHI in event body.", location: "US", baa: "On request" },
  { name: "Microsoft (Outlook/Graph API)", purpose: "Two-way calendar sync via OAuth.", data_categories: "Calendar event titles/times. No PHI in event body.", location: "US", baa: "On request" },
  { name: "Resend", purpose: "Transactional and opt-in marketing email.", data_categories: "Recipient email, subject line, sanitized body.", location: "US", baa: "Signed" },
  { name: "Twilio", purpose: "SMS reminders (opt-in only).", data_categories: "Recipient phone, sanitized SMS body.", location: "US", baa: "Signed" },
  { name: "Render", purpose: "Web service hosting, cron, workers, static site.", data_categories: "Application logs (no PHI). HTTP request metadata.", location: "US (Oregon)", baa: "Signed" },
  { name: "Cloudflare", purpose: "DNS, edge caching, DDoS protection.", data_categories: "HTTP request metadata only. No PHI.", location: "Global CDN", baa: "N/A — no PHI" },
];

export function Subprocessors() {
  useSeo({
    title: "Subprocessors — WeConsult Rx",
    description:
      "WeConsult Rx subprocessors: Supabase, AWS Bedrock, Adobe Sign, QuickBooks Online, Google, Microsoft, Resend, Twilio, Render, Cloudflare. BAA status per processor.",
    path: "/subprocessors",
  });
  return (
    <Section>
      <Container>
        <Link
          to="/trust"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Trust center
        </Link>
        <div className="mt-6 max-w-3xl">
          <Eyebrow>Trust center</Eyebrow>
          <Heading level={1} className="mt-3">
            Subprocessors
          </Heading>
          <Lede className="mt-6">
            Every third party that may process customer data. We notify
            customers at least 30 days before engaging a new subprocessor for
            any covered data category.
          </Lede>
        </div>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-canvas">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold text-ink-700">Subprocessor</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Purpose</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Data categories</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Location</th>
                <th className="px-5 py-3 font-semibold text-ink-700">BAA</th>
              </tr>
            </thead>
            <tbody>
              {SUBS.map((s, i) => (
                <tr key={s.name} className={i % 2 === 0 ? "bg-canvas/30" : ""}>
                  <td className="px-5 py-4 font-semibold text-ink-900 align-top">{s.name}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.purpose}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.data_categories}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.location}</td>
                  <td className="px-5 py-4 align-top">
                    <Badge tone={s.baa === "Signed" ? "forest" : "neutral"}>{s.baa}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-sm text-ink-500">
          Last updated 2026-05-30. Subscribe to subprocessor changes by emailing privacy@weconsultrx.com.
        </p>
      </Container>
    </Section>
  );
}
